import React, { useState, useEffect, useContext, useRef } from 'react'
import styled from 'styled-components'

import { usePaymentsContext } from '../../../context/paymentProvider'
import {
  CAF_MESSAGE_CODES,
  FIVE_SECONDS,
  PaymentTypes,
} from '../../../utils/constants'
import { useGroowthbookLocaleFeature } from '../../../hooks/useGrowthbook'
import {
  getUserCapabilitiesAndNationalIdStatus,
  resendActivationEmail,
} from '../../../adapters/user'
import { prevalidateWithdrawal } from '../../../adapters/payments'
import { getPaymentIQData } from '../../../adapters/payments'
import { AuthContext } from '../../../context/authProvider'
import { CapabilitiesDisclaimer } from '../../../atomic-components/atoms/capabilitiesDisclaimer'
import { Button } from '../../../atomic-components/atoms/button'
import { Modal } from '../../../atomic-components/atoms/modal'
import { toast } from '../../../atomic-components/atoms/toast'
import { Image } from '../../../atomic-components/atoms/image'
import { Spinner } from '../../../atomic-components/atoms/spinner'
import { CafIFrame } from '../../../atomic-components/molecules/cafIFrame'
import { useFeatureValue } from '@growthbook/growthbook-react'
import { themeMedia } from '../../../styles/theme'
import { KycStatus } from '../../../utils/constants'

import { useTranslation } from '../../../context/translationProvider'
import storageService from '../../../services/storageService'
import { getWebsiteUrl } from '../../../utils/getWebsiteUrl'

import selfieAndIdIcon from '../../../images/icon_selfie_and_id_gray.svg'
import selfieAndIdIconError from '../../../images/icon_selfie_and_id_red.svg'
import inProgressIcon from '../../../images/icon_circle_hourglass_orange.svg'
import errorIcon from '../../../images/icon_circle_error_red.svg'
import { If } from '../../../atomic-components/atoms/if'
import { BankAccountWithdrawFlow } from '../../../atomic-components/organisms/BankAccountWithdrawFlow'
import { getKYCStatusData } from '../../../adapters/kyc'
import { isMobileApp } from '../../../utils/mobileAppFunctionality'
import { useEventTracking } from '../../../context/eventTrackingProvider'

const Message = styled.div`
  text-align: center;
  padding: 2rem 0;
`

const VerificationMessage = styled.div`
  font-size: 1rem;
  text-align: center;
  padding: 1rem 0;
`

const VerificationStatusTitle = styled.div`
  font-size: 1.4rem;
  font-weight: 700;
  padding: 4rem 0 2rem;
`

const PaymentIQWrapper = styled.div`
  padding-top: 1rem;
  min-height: ${({ minHeight }) => `${minHeight}px`};

  iframe {
    width: 100%;
    min-height: ${({ minHeight }) => `${minHeight}px`};
    height: 100%;
  }
`

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
`

const Content = styled.div`
  width: 100%;
  padding: 1rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;

  small {
    padding: 0.5rem 0;
    text-align: center;
    display: block;
  }

  ${themeMedia.desktop`
        width: ${(props) => (props.isPage ? '770px' : '490px')};
        margin: 0 auto;
        padding: 0;
    `};
`

export const StyledImageWrapper = styled.div`
  position: relative;

  .main-img {
    width: 100px;
    height: 100px;

    ${themeMedia.desktop`
      width: 100px;
      height: 100px;
    `}
  }

  .little-icon {
    position: absolute;
    right: -5px;
    width: 30px;
    height: 30px;

    ${themeMedia.desktop`
      width: 30px;
      height: 30px;
    `}
  }
`

const APPROVED = 'APPROVED'

const Withdraw = ({
  onClose,
  isOpen,
  hideOverlay,
  isMobile,
  isPage,
  setHideHeader,
}) => {
  const [user, setUser] = useState(storageService.getUser())
  const [isWaitingVerification, setIsWaitingVerification] = useState(
    storageService.getIsWaitingVerification()
  )
  const { translate } = useTranslation()
  const [methods, setMethods] = useState()
  const [isWithdrawalAllowed, setIsWithdrawalAllowed] = useState(false)
  const [mustRequestIdVerification, setMustRequestIdVerification] =
    useState(false)
  const [idVerificationStarted, setIdVerificationStarted] = useState(false)
  const [isLoadingSettings, setIsLoadingSettings] = useState(true)

  const paymentIQFeature = useGroowthbookLocaleFeature('payment_iq')
  const requestIdVerificationFeature = useFeatureValue(
    'withdrawal_unverified_player_request_kyc'
  )
  const [paymentIQURL, setPaymentIQURL] = useState('')

  const {
    user: safeUser,
    safeSetUser,
    getUserWallet,
    updateUser,
  } = useContext(AuthContext)

  const isUserUsingBRL = user?.wallet?.currency?.short_code === 'BRL'
  const isPaymentIQEnabled = paymentIQFeature && !isUserUsingBRL
  const websiteURL = getWebsiteUrl()

  const isBankAccountFlowEnabled = useGroowthbookLocaleFeature(
    'fe_igp_bank_accounts_regulation_flow'
  )

  const {
    withdrawalPaymentMethods,
    getPaymentsByType,
    setCurrentMethod,
  } = usePaymentsContext()

  const { trackEvent, EVENT_NAMES, EVENT_LOCATIONS } = useEventTracking()

  const resendEmailActivation = async () => {
    const response = await resendActivationEmail(
      user.email,
      `${websiteURL}activate/`
    )

    if (response.ok) toast.success(translate('account.resendEmailSuccess'))
  }

  const getUserStatuses = async () => {
    const storedUser = storageService.getUser()

    const response = await getUserCapabilitiesAndNationalIdStatus()

    if (response.ok) {
      const { withdrawal, national_id_status } = response.data.data
      const updatedUser = {
        ...storedUser,
        national_id_status,
        user_capabilities: {
          ...storedUser?.user_capabilities,
          withdrawal,
        },
      }

      setUser(updatedUser)
      updateUser(updatedUser)
    }
  }

  const checkIfUserNeedsToVerify = () => {
    if (!requestIdVerificationFeature) return
    const interval = setInterval(() => {
      getUserIdVerificationStatus()
      safeSetUser()
    }, 30000)
    return interval
  }
  const intervalId = useRef()

  const getUserIdVerificationStatus = async () => {
    const response = await prevalidateWithdrawal()
    if (response.ok) {
      if (!requestIdVerificationFeature) return
      setMustRequestIdVerification(response.data?.mustRequestIdVerification)
      if (!response.data?.mustRequestIdVerification) {
        storageService.clearIsWaitingVerification()
        setIsWaitingVerification(false)
      }
    }
  }

  const getInitialSettings = async () => {
    setIsLoadingSettings(true)
    await getUserStatuses()
    await getUserIdVerificationStatus()
    setIsLoadingSettings(false)
  }

  const closeCafIFrame = (status) => {
    if (status !== CAF_MESSAGE_CODES.ONBOARDING_FINISHED) return
    const poolingInterval = setInterval(async () => {
      const response = await getKYCStatusData(user.id)
      if (response?.data?.status === APPROVED) {
        clearInterval(poolingInterval)
        storageService.setIsWaitingVerification()
        setIsWaitingVerification(true)
      }
    }, FIVE_SECONDS)
  }

  const startIdVerification = () => {
    setIdVerificationStarted(true)
    storageService.clearIsWaitingVerification()
    setIsWaitingVerification(false)
  }

  useEffect(() => {
    if (isOpen) {
      const filterMethods = async () => {
        await getPaymentsByType(PaymentTypes.WITHDRAW)
      }
      filterMethods()
    }
  }, [isOpen])

  useEffect(() => {
    if (withdrawalPaymentMethods) {
      const sortedMethods = withdrawalPaymentMethods.sort(
        (a, b) => a.orderNumber - b.orderNumber
      )
      setMethods(sortedMethods)
    } else {
      setCurrentMethod(null)
    }
  }, [withdrawalPaymentMethods])

  useEffect(() => {
    setIsWithdrawalAllowed(
      user?.user_capabilities?.withdrawal && !mustRequestIdVerification
    )
  }, [user, isLoadingSettings, mustRequestIdVerification])

  useEffect(() => {
    const getPaymentIQURL = async () => {
      const { ok, data } = await getPaymentIQData('withdrawal')
      if (ok) setPaymentIQURL(data?.url)
    }

    if (isPaymentIQEnabled) {
      getPaymentIQURL()
    }
  }, [isPaymentIQEnabled])

  const [paymentIQHeight, setPaymentIQHeight] = useState()

  const listenPaymentIQ = (event) => {
    if (event?.data?.eventType === 'TX_SUCCESS') {
      getUserWallet()
    }

    if (event?.data?.eventType === 'APP_SET_HEIGHT') {
      setPaymentIQHeight(event?.data?.payload?.height)
    }
  }

  useEffect(() => {
    if (isPaymentIQEnabled && getUserWallet) {
      window.addEventListener('message', listenPaymentIQ, false)

      return () => {
        window.removeEventListener('message', listenPaymentIQ)
      }
    }
  }, [isPaymentIQEnabled, getUserWallet])

  useEffect(() => {
    intervalId.current = checkIfUserNeedsToVerify()

    getInitialSettings()

    return () => {
      clearInterval(intervalId.current)
    }
  }, [])

  useEffect(() => {
    if (isOpen)
      trackEvent(EVENT_NAMES.SCREEN_LOADED, {
        screen_name: EVENT_LOCATIONS.WITHDRAWAL,
      })
  }, [isOpen])

  const IS_BANK_ACCOUNT_FLOW_ENABLED =
    isBankAccountFlowEnabled && !mustRequestIdVerification

  if (isLoadingSettings && !IS_BANK_ACCOUNT_FLOW_ENABLED) {
    return (
      <Modal
        theme="payments"
        title={translate('payments.withdraw.title')}
        id="withdraw-modal"
        isOpen={isOpen}
        onClose={onClose}
        paymentIQMinHeight={paymentIQHeight ? paymentIQHeight + 95 : null}
        hideOverlay={hideOverlay ? hideOverlay : null}
        isMobile={isMobile ? isMobile : null}
        isPage={isPage ? isPage : null}
      >
        <Content>
          <Spinner style={{ margin: '4rem 0' }} />
        </Content>
      </Modal>
    )
  }

  if (safeUser?.kyc_status === KycStatus.REJECTED) {
    return (
      <Modal
        theme="payments"
        title={translate('payments.withdraw.title')}
        id="withdraw-modal"
        isOpen={isOpen}
        onClose={onClose}
        paymentIQMinHeight={paymentIQHeight ? paymentIQHeight + 95 : null}
        hideOverlay={hideOverlay ? hideOverlay : null}
        isMobile={isMobile ? isMobile : null}
        isPage={isPage ? isPage : null}
      >
        {!idVerificationStarted && (
          <Wrapper>
            <Content>
              <VerificationStatusTitle>
                {translate('account.WithdrawalVerificationErrorTitle')}
              </VerificationStatusTitle>

              <StyledImageWrapper>
                <Image url={selfieAndIdIconError} className="main-img" />
                <img src={errorIcon} className="little-icon" />
              </StyledImageWrapper>

              <VerificationMessage>
                {translate('account.WithdrawalVerificationErrorDescription')}
              </VerificationMessage>
              <Button
                expand
                style={{ 'margin-top': '2rem' }}
                onClick={startIdVerification}
              >
                {translate('common.tryAgain')}
              </Button>
              <Button
                expand
                bordered
                dark
                style={{ 'margin-top': '1rem' }}
                onClick={onClose}
              >
                {translate('common.later')}
              </Button>
            </Content>
          </Wrapper>
        )}

        {idVerificationStarted && (
          <CafIFrame
            minHeight={'75vh'}
            chosenDocument={'IDENTIFICATION_AND_SELFIE'}
            closeIFrame={closeCafIFrame}
          />
        )}
      </Modal>
    )
  }

  const IS_SELFIE_AND_ID_REQUIRED =
    mustRequestIdVerification &&
    !idVerificationStarted &&
    !isWaitingVerification &&
    !isMobileApp()

  const IS_REGULAR_WITHDRAW_FLOW_ENABLED =
    !isPaymentIQEnabled &&
    isWithdrawalAllowed &&
    !isBankAccountFlowEnabled &&
    !mustRequestIdVerification

  if (IS_BANK_ACCOUNT_FLOW_ENABLED) {
    if (setHideHeader) {
      setHideHeader(false)
    }

    return (
      <BankAccountWithdrawFlow
        method={methods?.[0]}
        isOpen={isOpen}
        onClose={onClose}
        paymentIQHeight={paymentIQHeight}
        hideOverlay={hideOverlay}
        isMobile={isMobile}
        isPage={isPage}
      />
    )
  }

  return (
    <Modal
      theme="payments"
      title={translate('payments.withdraw.title')}
      id="withdraw-modal"
      isOpen={isOpen}
      onClose={onClose}
      paymentIQMinHeight={paymentIQHeight ? paymentIQHeight + 95 : null}
      hideOverlay={hideOverlay ? hideOverlay : null}
      isMobile={isMobile ? isMobile : null}
      isPage={isPage ? isPage : null}
    >
      {mustRequestIdVerification && isWaitingVerification && (
        <Wrapper>
          <Content>
            <VerificationStatusTitle>
              {translate('account.WithdrawalVerificationAwaitTitle')}
            </VerificationStatusTitle>

            <StyledImageWrapper>
              <Image url={selfieAndIdIcon} className="main-img" />
              <img src={inProgressIcon} className="little-icon" />
            </StyledImageWrapper>

            <VerificationMessage>
              {translate('account.WithdrawalVerificationAwaitDescription')}
            </VerificationMessage>
            <Button expand style={{ 'margin-top': '2rem' }} onClick={onClose}>
              {translate('common.close')}
            </Button>
          </Content>
        </Wrapper>
      )}

      <If
        condition={IS_SELFIE_AND_ID_REQUIRED}
        render={() => (
          <Wrapper>
            <Content>
              <VerificationStatusTitle>
                {translate('account.WithdrawalRequestIdVerification')}
              </VerificationStatusTitle>

              <Image url={selfieAndIdIcon} />

              <VerificationMessage>
                {translate('account.WithdrawalVerificationRequestDescription')}
              </VerificationMessage>
              <VerificationMessage>
                {translate('account.WithdrawalVerificationRequestAwait')}
              </VerificationMessage>
              <Button
                expand
                style={{ 'margin-top': '2rem' }}
                onClick={startIdVerification}
              >
                {translate('common.start')}
              </Button>
            </Content>
          </Wrapper>
        )}
      />

      {mustRequestIdVerification &&
        idVerificationStarted &&
        !isWaitingVerification && (
          <CafIFrame
            minHeight={'75vh'}
            chosenDocument={'IDENTIFICATION_AND_SELFIE'}
            closeIFrame={closeCafIFrame}
          />
        )}

      {!isWithdrawalAllowed &&
        !mustRequestIdVerification &&
        !isBankAccountFlowEnabled && (
          <CapabilitiesDisclaimer onClose={onClose} />
        )}

      <If
        condition={IS_REGULAR_WITHDRAW_FLOW_ENABLED}
        render={() => (
          <BankAccountWithdrawFlow
          method={methods?.[0]}
          isOpen={isOpen}
          onClose={onClose}
          paymentIQHeight={paymentIQHeight}
          hideOverlay={hideOverlay}
          isMobile={isMobile}
          isPage={isPage}
        />
        )}
      />

      <If
        condition={!IS_SELFIE_AND_ID_REQUIRED}
        render={() => (
          <>
            {isPaymentIQEnabled && paymentIQURL && isWithdrawalAllowed && (
              <PaymentIQWrapper minHeight={paymentIQHeight}>
                <iframe src={paymentIQURL} />
              </PaymentIQWrapper>
            )}

            {methods &&
              methods.length === 0 &&
              !isPaymentIQEnabled &&
              isWithdrawalAllowed &&
              user?.user_status?.name?.toLowerCase() !== 'pending' && (
                <Message>
                  {translate('account.paymentMethodsNotAvailable').replace(
                    '{0}',
                    user?.wallet?.currency?.symbol ||
                      user.wallet.currency.short_code
                  )}
                </Message>
              )}

            {user?.user_status?.name?.toLowerCase() === 'pending' && (
              <Message>
                {translate('account.not-verified')}
                <Button
                  style={{ margin: '1rem 0' }}
                  onClick={resendEmailActivation}
                >
                  {translate('account.resend-email')}
                </Button>
              </Message>
            )}
          </>
        )}
      />
    </Modal>
  )
}

export default Withdraw
